import { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Confirm, useGetList, useNotify, usePermissions, useRecordContext } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage } from "../shared/api-error";
import {
  describeConcession,
  formatMonthYear,
  formatRupees,
  summarizeDueChanges,
  type ConcessionLike,
  type DueChanges,
} from "../shared/fee-format";
import {
  MADRASA_FEE_SETUP_CREATE,
  MADRASA_FEE_SETUP_DELETE,
  MADRASA_FEE_SETUP_VIEW,
  MADRASA_FEES_VIEW,
} from "../shared/madrasa-permissions";

type EnrollmentRecord = { id: string; status: string; enrolledOn: string; academicYearId: string };
type ConcessionRow = ConcessionLike & { id: string; reason: string };
type DueRow = {
  id: string;
  feeHeadName: string;
  periodLabel: string;
  dueDate: string;
  grossAmount: number;
  concessionAmount: number;
  netAmount: number;
  paidAmount: number;
  outstanding: number;
  status: "PENDING" | "PARTIAL" | "PAID" | "CANCELLED";
  overdue: boolean;
};
type HeadRow = { id: string; name: string };

const STATUS_CHIP: Record<
  DueRow["status"],
  { label: string; color: "default" | "warning" | "success" }
> = {
  PENDING: { label: "Unpaid", color: "default" },
  PARTIAL: { label: "Part paid", color: "warning" },
  PAID: { label: "Paid", color: "success" },
  CANCELLED: { label: "Cancelled", color: "default" },
};

function AddConcessionDialog({
  open,
  onClose,
  onSaved,
  enrollment,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (changes: DueChanges | undefined) => void;
  enrollment: EnrollmentRecord;
}) {
  const notify = useNotify();
  const heads = useGetList<HeadRow>("feeHeads", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "sortOrder", order: "ASC" },
  });
  const [feeHeadId, setFeeHeadId] = useState("");
  const [type, setType] = useState<"PERCENT" | "FIXED">("PERCENT");
  const [value, setValue] = useState("");
  const [from, setFrom] = useState(""); // <input type="month"> -> YYYY-MM
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const valueNumber = Number(value);
  const valueProblem =
    value.trim() === ""
      ? null
      : !/^\d+$/.test(value.trim())
        ? "Whole numbers only"
        : type === "PERCENT" && (valueNumber < 1 || valueNumber > 100)
          ? "Between 1 and 100"
          : valueNumber < 1
            ? "Must be at least 1"
            : null;
  const canSave = value.trim() !== "" && !valueProblem && reason.trim() !== "" && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      const response = await callApi({
        location: "feeConcessions",
        method: "POST",
        data: {
          enrollmentId: enrollment.id,
          ...(feeHeadId ? { feeHeadId } : {}),
          type,
          value: valueNumber,
          ...(from ? { effectiveFrom: `${from}-01` } : {}),
          reason: reason.trim(),
        },
      });
      onSaved((response.data as { dueChanges?: DueChanges }).dueChanges);
      setValue("");
      setReason("");
      setFrom("");
      setFeeHeadId("");
      onClose();
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Could not add the concession"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add a concession</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            select
            label="Applies to"
            value={feeHeadId}
            onChange={(e) => setFeeHeadId(e.target.value)}
          >
            <MenuItem value="">All fees</MenuItem>
            {(heads.data ?? []).map((h) => (
              <MenuItem key={h.id} value={h.id}>
                {h.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value as "PERCENT" | "FIXED")}
          >
            <MenuItem value="PERCENT">Percentage off each due</MenuItem>
            <MenuItem value="FIXED">Fixed rupees off each due</MenuItem>
          </TextField>
          <TextField
            label={type === "PERCENT" ? "Percentage (1-100)" : "Rupees off"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={Boolean(valueProblem)}
            helperText={valueProblem ?? " "}
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
          />
          <TextField
            label="Starting from"
            type="month"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText={`Leave empty to start from the enrolment month (${formatMonthYear(enrollment.enrolledOn)}). Applies to dues whose period starts on or after this month.`}
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText="For example: orphan, needy family, staff child."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!canSave}>
          {busy ? "Saving…" : "Add concession"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Concessions and generated fee dues for one enrollment, shown under the enrollment form. */
export function EnrollmentFeesSection() {
  const record = useRecordContext<EnrollmentRecord>();
  const { permissions } = usePermissions();
  const notify = useNotify();
  const canSeeConcessions = hasPermission(permissions, MADRASA_FEE_SETUP_VIEW);
  const canAddConcession = hasPermission(permissions, MADRASA_FEE_SETUP_CREATE);
  const canRemoveConcession = hasPermission(permissions, MADRASA_FEE_SETUP_DELETE);
  const canSeeDues = hasPermission(permissions, MADRASA_FEES_VIEW);

  const [concessions, setConcessions] = useState<ConcessionRow[]>([]);
  const [dues, setDues] = useState<DueRow[]>([]);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ConcessionRow | null>(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    if (!record) return;
    try {
      if (canSeeConcessions) {
        const r = await callApi({
          location: "feeConcessions",
          method: "GET",
          data: { enrollmentId: record.id },
        });
        setConcessions(((r.data as { rows?: ConcessionRow[] }).rows ?? []) as ConcessionRow[]);
      }
      if (canSeeDues) {
        const r = await callApi({
          location: "feeDues",
          method: "GET",
          data: { enrollmentId: record.id, limit: 1000 },
        });
        setDues(((r.data as { rows?: DueRow[] }).rows ?? []) as DueRow[]);
      }
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Could not load the fees"), { type: "error" });
    }
  }, [record, canSeeConcessions, canSeeDues, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!record || (!canSeeConcessions && !canSeeDues)) return null;

  const afterChange = (changes: DueChanges | undefined) => {
    setNotice(summarizeDueChanges(changes));
    void load();
  };

  const removeConcession = async () => {
    if (!removing) return;
    try {
      const r = await callApi({ location: "feeConcessions", method: "DELETE", id: removing.id });
      afterChange((r.data as { dueChanges?: DueChanges }).dueChanges);
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Could not remove the concession"), { type: "error" });
    } finally {
      setRemoving(null);
    }
  };

  const live = dues.filter((d) => d.status !== "CANCELLED");
  const totals = live.reduce(
    (t, d) => ({ net: t.net + d.netAmount, paid: t.paid + d.paidAmount }),
    { net: 0, paid: 0 }
  );

  return (
    <Box sx={{ maxWidth: 900, mt: 3 }}>
      {notice && (
        <Alert severity="success" onClose={() => setNotice("")} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}

      {canSeeConcessions && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}
          >
            <Typography variant="h6">Concessions</Typography>
            {canAddConcession && record.status === "ACTIVE" && (
              <Button size="small" variant="outlined" onClick={() => setAdding(true)}>
                Add concession
              </Button>
            )}
          </Box>
          {concessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No concessions. This student pays the full fee.
            </Typography>
          ) : (
            <Table size="small">
              <TableBody>
                {concessions.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      {describeConcession(c)}
                      <Typography variant="body2" color="text.secondary">
                        {c.reason}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width={48}>
                      {canRemoveConcession && record.status === "ACTIVE" && (
                        <IconButton
                          size="small"
                          aria-label="Remove concession"
                          onClick={() => setRemoving(c)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {canSeeDues && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Fee dues
          </Typography>
          {dues.length === 0 ? (
            <Alert severity="info">
              No dues yet. They appear once fees are set for this class and year, and synced from
              the Fee structures page.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Total {formatRupees(totals.net)} · paid {formatRupees(totals.paid)} · outstanding{" "}
                {formatRupees(totals.net - totals.paid)}
              </Typography>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fee</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Concession</TableCell>
                      <TableCell align="right">To pay</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Outstanding</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dues.map((d) => {
                      const chip = STATUS_CHIP[d.status];
                      return (
                        <TableRow
                          key={d.id}
                          sx={d.status === "CANCELLED" ? { opacity: 0.55 } : undefined}
                        >
                          <TableCell>{d.feeHeadName}</TableCell>
                          <TableCell>{d.periodLabel}</TableCell>
                          <TableCell align="right">{formatRupees(d.grossAmount)}</TableCell>
                          <TableCell align="right">
                            {d.concessionAmount ? `-${formatRupees(d.concessionAmount)}` : "—"}
                          </TableCell>
                          <TableCell align="right">{formatRupees(d.netAmount)}</TableCell>
                          <TableCell align="right">{formatRupees(d.paidAmount)}</TableCell>
                          <TableCell align="right">
                            {d.status === "CANCELLED" ? "—" : formatRupees(d.outstanding)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={chip.color}
                              label={chip.label}
                              variant={d.status === "PENDING" ? "outlined" : "filled"}
                            />
                            {d.overdue && (
                              <Chip size="small" color="error" label="Overdue" sx={{ ml: 0.5 }} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      <AddConcessionDialog
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={afterChange}
        enrollment={record}
      />
      <Confirm
        isOpen={removing !== null}
        title="Remove this concession?"
        content={
          removing
            ? `${describeConcession(removing)}. The student's unpaid dues go back to the full amount; dues already paid are not changed.`
            : ""
        }
        onConfirm={removeConcession}
        onClose={() => setRemoving(null)}
      />
    </Box>
  );
}
